import { Button } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Checkbox, Input, Modal } from 'antd';
import IconButton from '@mui/material/IconButton';
import { PlusCircleOutlined } from '@ant-design/icons';
import React, { useEffect, useRef, useState } from 'react';
import { useNotify } from 'react-admin';

import { useRequest } from '@hooks';
import { adminHttp } from '@network';
import { AdminComment } from '@types';
import { dateFormatter, rbac } from '@utils';
import { Scroll, ConfirmDelete, BlueOutlineButton, BlueButton } from '@components';
import dayjs from 'dayjs';

interface CommentsWidgetProps {
  comments: AdminComment[];
  partnerClass: string;
  partnerId: number;
  editable?: boolean;
}

export const CommentsWidget = (props: CommentsWidgetProps) => {
  const { partnerClass, partnerId } = props;
  const notify = useNotify();
  const [comments, setComments] = useState<AdminComment[]>(props.comments);
  const [editComment, setEditComment] = useState<AdminComment>();
  const cls = useStyles();
  const textRef = useRef<any>();
  const [text, setText] = useState('');
  const [showToPartner, setShowToPartner] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const hasAdminNote = rbac.hasAdminNotePermission();

  useEffect(() => {
    setComments(props.comments);
  }, [props.comments]);

  const showModal = () => {
    setIsModalVisible(true);
    setTimeout(() => void textRef.current?.focus(), 500);
  };

  const hideModal = () => {
    setEditComment(undefined);
    setIsModalVisible(false);
  };

  const { loading, submit } = useRequest(async () => {
    if (!text && !editComment?.comment) {
      textRef.current?.focus();

      return;
    }

    const savedComment = await adminHttp.addComment({
      id: editComment?.id,
      comment: editComment ? editComment.comment : text,
      partnerClass,
      partnerId,
      showToPartner: editComment ? editComment.showToPartner : showToPartner,
    });

    if (editComment) {
      notify('Comment updated');
      const nextComments = comments.map((comment: AdminComment) => {
        if (comment.id === savedComment.id) return savedComment;
        return comment;
      });
      setComments(nextComments);
    } else {
      notify('Comment added');
      setComments([...comments, savedComment]);
    }

    setText('');
    hideModal();
  });

  const { submit: remove } = useRequest(async (id: number) => {
    if (!id) {
      return;
    }
    await adminHttp.removeComment(id);
    setComments(comments.filter(comment => comment.id !== id));
    notify('Comment deleted');
  });

  const sortedComments = (comments ?? []).sort((a, b) =>
    dayjs(b.updatedAt).toDate().getTime() - dayjs(a.updatedAt).toDate().getTime());

  const openEditComment = (comment: AdminComment) => {
    setEditComment(comment);
    showModal();
  };

  return (
    <div className={cls.wrap} onClick={e => e.stopPropagation()}>
      <Scroll height={225} paddingRight={25}>
        {sortedComments.map(comment => (
          <div className={cls.item} key={comment.id}>
            <div className={cls.comment}>
              {comment.comment}
            </div>
            <div className={cls.row}>
              <b>{comment.adminName}</b>
              {comment.showToPartner && <b>✓ Share with partners</b>}
              <span>{dateFormatter.toDateTime(comment.updatedAt, true)}</span>
            </div>
            {hasAdminNote ? (
              <>
                <IconButton className={cls.editBtn} onClick={() => openEditComment(comment)}>
                  <EditIcon />
                </IconButton>
                <ConfirmDelete
                  title="Do you really want to delete the comment?"
                  cb={() => remove(comment.id)}
                >
                  <IconButton className={cls.deleteBtn}>
                    <DeleteIcon />
                  </IconButton>
                </ConfirmDelete>
              </>
            ) : null}
          </div>
        ))}
      </Scroll>

      {hasAdminNote ? (
        <div className={cls.plusRow}>
          <Button
            className={cls.plusBtn}
            onClick={showModal}
            variant="contained"
            color="secondary"
          >
            <PlusCircleOutlined /> Admin Notes
          </Button>
        </div>
      ) : null}

      <Modal
        open={isModalVisible}
        title={editComment ? 'Edit note' : 'Add note'}
        okText={editComment ? 'Update' : 'Save'}
        onOk={submit}
        confirmLoading={loading}
        onCancel={hideModal}
        footer={(
          <div className={cls.footer}>
            <Checkbox
              checked={editComment ? editComment.showToPartner : showToPartner}
              onChange={e => {
                if (editComment) {
                  setEditComment({ ...editComment, showToPartner: e.target.checked });
                } else {
                  setShowToPartner(e.target.checked);
                }
              }}
              className={cls.partnerCheckbox}
            >Share with partners</Checkbox>
            <div>
              <BlueOutlineButton onClick={hideModal} size="small">Cancel</BlueOutlineButton>
              {editComment ? (
                <BlueButton onClick={submit}>Update</BlueButton>
              ) : (
                <ConfirmDelete title="Are you sure?"
                  cb={showToPartner ? submit : () => {}}
                  disabled={!showToPartner}>
                  <BlueButton
                    className={cls.saveBtn}
                    onClick={!showToPartner ? submit : undefined}
                  >Save</BlueButton>
                </ConfirmDelete>
              )}
            </div>
          </div>
        )}
      >
        <Input.TextArea
          className={cls.commentField}
          ref={textRef}
          value={editComment ? editComment.comment : text}
          onChange={e => {
            if (editComment) {
              setEditComment({ ...editComment, comment: e.target.value });
            } else {
              setText(e.target.value);
            }
          }}
          autoFocus
        />
      </Modal>
    </div>
  );
};

const useStyles = makeStyles({
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingLeft: 8,
    paddingRight: 8,
    '& button': {
      width: '80px !important',
      marginLeft: 15,
    },
  },
  partnerCheckbox: {},
  wrap: {
    display: 'flex',
    flexWrap: 'wrap',
    marginLeft: -15,
    fontFamily: 'Outfil, sans-serif',
  },
  deleteBtn: {
    padding: 6,
    position: 'absolute',
    top: 0,
    right: 0,
  },
  editBtn: {
    padding: 6,
    position: 'absolute',
    top: 0,
    right: 26,
  },
  item: {
    position: 'relative',
    width: '100%',
    marginLeft: 15,
    marginBottom: 10,
  },
  comment: {
    borderRadius: 8,
    border: '1px solid var(--logo-color)',
    fontSize: 13,
    lineHeight: 1.05,
    padding: '12px 50px 12px 20px',
    color: 'var(--text-color)',
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '3px 5px 0',
    fontSize: 12,
  },
  plusRow: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  plusBtn: {
    marginLeft: 15,
    textTransform: 'none',
    backgroundColor: 'var(--primary-color)',
    '&:hover': {
      backgroundColor: 'var(--primary-color)',
    },
    '& svg': {
      marginRight: 10,
    },
  },
  saveBtn: {
    backgroundColor: 'var(--primary-color)',
    '&:hover': {
      backgroundColor: 'var(--primary-color)',
    },
  },
  closeBtn: {
    position: 'absolute',
    top: -8,
    right: -8,
    borderRadius: '50%',
    cursor: 'pointer',
  },
  closeBtnIcon: {
    fontSize: 18,
    color: '#f5222d',
  },
  commentField: {
    backgroundColor: 'var(--background-color)',
    color: 'var(--text-color)',

    '&:focus': {
      borderColor: 'var(--background-color)',
    },
    '&:hover': {
      borderColor: 'var(--background-color)',
    },
  },
});

const DeleteIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
    <path d="M9 0C4.03021 0 0 4.03021 0 9C0 13.9698 4.03021 18 9 18C13.9698 18 18 13.9698 18 9C17.992 4.03021 13.9658 0.00399822 9 0Z"
      style={{ fill: 'var(--primary-color)' }} />
    <path d="M12.3863 5.60962C12.2303 5.45369 11.9785 5.45369 11.8225 5.60962L8.99978 8.43237L6.17704 5.60962C6.0211 5.45369 5.76922 5.45369 5.61329 5.60962C5.45736 5.76555 5.45736 6.01744 5.61329 6.17337L8.43603 8.99612L5.61329 11.8189C5.45736 11.9748 5.45736 12.2267 5.61329 12.3826C5.76922 12.5385 6.0211 12.5385 6.17704 12.3826L8.99978 9.55987L11.8225 12.3826C11.9785 12.5385 12.2303 12.5385 12.3863 12.3826C12.5422 12.2267 12.5422 11.9748 12.3863 11.8189L9.56353 8.99612L12.3863 6.17337C12.5422 6.01744 12.5422 5.76555 12.3863 5.60962Z"
      fill="white" />
  </svg>
);

const EditIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9 0C4.03021 0 0 4.03021 0 9C0 13.9698 4.03021 18 9 18C13.9698 18 18 13.9698 18 9C17.992 4.03021 13.9658 0.00399822 9 0Z"
      style={{ fill: 'var(--primary-color)' }} />
    <path d="M9.99013 4.87655L12.4434 6.4261L7.86212 13.6792L5.40886 12.1297L9.99013 4.87655Z"
      fill="white" />
    <path d="M11.5172 2.45885C11.9389 1.79122 12.8299 1.59688 13.5074 2.02477C14.1848 2.45267 14.3922 3.34077 13.9705 4.0084L13.2069 5.21725L10.7537 3.6677L11.5172 2.45885Z"
      fill="white" />
    <path d="M4.72663 15.9266L7.48035 14.2836L5.02709 12.7341L4.72663 15.9266Z" fill="white" />
  </svg>
);

